import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, AlertTriangle, Loader2, MessageCircle, Leaf } from "lucide-react";

const WHATSAPP_NUMBER = (process.env.REACT_APP_LEAD_WHATSAPP || "").replace(/\D+/g, "");
const WHATSAPP_HELP_MSG = "Olá! Meu link de acesso ao painel do paciente expirou. Pode me enviar um novo?";

export default function MagicLinkConsumePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "loading", error: null });
  // Guard against React 18 StrictMode double-invoking the effect in dev — a
  // second call would hit the (already used) token and force the error path.
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return;
    consumedRef.current = true;
    (async () => {
      try {
        const { data } = await api.post(`/auth/magic-link/${token}`);
        if (data.access_token) localStorage.setItem("cs_access_token", data.access_token);
        await refresh();
        setState({ status: "ok", error: null });
        setTimeout(() => navigate("/app", { replace: true }), 800);
      } catch (e) {
        const msg = formatApiError(e.response?.data?.detail) || "Não foi possível validar seu link.";
        setState({ status: "error", error: msg });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (state.status === "loading") {
    return (
      <div className="min-h-screen bg-background grid place-items-center px-4" data-testid="magic-link-loading">
        <div className="max-w-sm w-full text-center">
          <Loader2 className="w-8 h-8 text-primary mx-auto animate-spin mb-4" />
          <div className="overline text-gold mb-2 tracking-[0.24em]">Centro de Performance Farmacêutica</div>
          <h1 className="brand-wordmark uppercase text-xl font-bold text-primary">Entrando…</h1>
          <p className="text-sm text-muted-foreground mt-2">Validando seu acesso.</p>
        </div>
      </div>
    );
  }

  if (state.status === "ok") {
    return (
      <div className="min-h-screen bg-background grid place-items-center px-4" data-testid="magic-link-success">
        <div className="max-w-sm w-full text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-500/10 text-emerald-600 grid place-items-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <div className="overline text-gold mb-2 tracking-[0.24em]">Acesso liberado</div>
          <h1 className="brand-wordmark uppercase text-xl font-bold text-primary">Redirecionando…</h1>
        </div>
      </div>
    );
  }

  // Error state
  const whatsappHref = WHATSAPP_NUMBER
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_HELP_MSG)}`
    : null;

  return (
    <div className="min-h-screen bg-background grid place-items-center px-4 py-10" data-testid="magic-link-error">
      <div className="max-w-md w-full text-center">
        <div className="w-14 h-14 rounded-full bg-amber-500/10 text-amber-600 grid place-items-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8" />
        </div>
        <div className="overline text-gold mb-2 tracking-[0.24em]">Link indisponível</div>
        <h1 className="brand-wordmark uppercase text-xl md:text-2xl font-bold text-primary leading-tight">
          Não conseguimos validar seu acesso
        </h1>
        <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
          {state.error}
        </p>

        <a
          href={whatsappHref || "#"}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => { if (!whatsappHref) e.preventDefault(); }}
          className={`mt-6 inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 text-sm font-semibold w-full sm:w-auto min-h-[48px] transition-colors ${
            whatsappHref
              ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
              : "bg-muted text-muted-foreground cursor-not-allowed"
          }`}
          data-testid="magic-link-whatsapp-help"
        >
          <MessageCircle className="w-4 h-4" /> Pedir novo acesso no WhatsApp
        </a>

        <div className="mt-4">
          <button
            onClick={() => navigate("/login")}
            className="text-xs text-muted-foreground underline hover:text-primary"
            data-testid="magic-link-back-login"
          >
            Ou entrar com e-mail e senha →
          </button>
        </div>

        <div className="mt-10 opacity-60 flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <Leaf className="w-3.5 h-3.5" /> Centro de Performance Farmacêutica
        </div>
      </div>
    </div>
  );
}
